<template>
    <Head title="Access activity" />

    <TailwindLayout title="Access activity">
        <div class="bg-white rounded-lg shadow-sm dark:bg-zinc-800">

            <!-- Page header -->
            <div class="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-zinc-700">
                <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 class="text-3xl font-semibold leading-tight tracking-tight text-gray-900 dark:text-white">
                            Access activity
                        </h1>
                        <p class="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                            Recent visits recorded while invited agents browse listings you assigned as view-only. Events are scoped to your account only.
                        </p>
                    </div>
                    <Link
                        :href="route('collaborators.index')"
                        class="shrink-0 self-start text-sm font-semibold text-rent-risk-blue-600 hover:text-rent-risk-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                        Manage access &amp; assignments
                    </Link>
                </div>
            </div>

            <div class="px-6 py-6">

                <!-- Empty state -->
                <section
                    v-if="collaborators.length === 0"
                    class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-10 text-center dark:border-zinc-700 dark:bg-zinc-700/20"
                >
                    <font-awesome-icon :icon="faScreenUsers" class="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                    <p class="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">No active collaborators yet</p>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        No active collaborators to report activity for yet.
                    </p>
                </section>

                <!-- Activity table -->
                <section v-else class="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                        <thead class="bg-gray-50 dark:bg-zinc-700/50">
                            <tr>
                                <th scope="col" class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                                    Agent
                                </th>
                                <th scope="col" class="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 sm:table-cell">
                                    Email
                                </th>
                                <th scope="col" class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                                    Events
                                </th>
                                <th scope="col" class="hidden px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 md:table-cell">
                                    Last activity
                                </th>
                                <th scope="col" class="sr-only">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-800">
                            <tr
                                v-for="row in collaborators"
                                :key="row.membership_id"
                                class="hover:bg-gray-50 dark:hover:bg-zinc-700/40 transition-colors"
                            >
                                <td class="px-4 py-3">
                                    <div class="flex items-center gap-3">
                                        <div
                                            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white select-none"
                                            :style="{ backgroundColor: avatarColor(row.invited_agent?.name) }"
                                            aria-hidden="true"
                                        >
                                            {{ initials(row.invited_agent?.name) }}
                                        </div>
                                        <span class="text-sm font-medium text-gray-900 dark:text-white">
                                            {{ row.invited_agent?.name }}
                                        </span>
                                    </div>
                                </td>
                                <td class="hidden px-4 py-3 text-sm text-gray-600 dark:text-gray-400 sm:table-cell">
                                    {{ row.invited_agent?.email }}
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 text-right">
                                    <span class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700 dark:bg-zinc-700 dark:text-gray-300">
                                        {{ row.event_count }}
                                    </span>
                                </td>
                                <td class="hidden whitespace-nowrap px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-400 md:table-cell">
                                    {{ row.last_activity_display ?? '—' }}
                                </td>
                                <td class="whitespace-nowrap px-4 py-3 text-right text-sm">
                                    <Link
                                        v-if="row.invited_agent?.id"
                                        :href="route('collaborators.activity.show', { collaboratorAgent: row.invited_agent.id })"
                                        class="font-semibold text-rent-risk-blue-600 hover:text-rent-risk-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                        View log
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </section>

            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faScreenUsers } from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

defineProps({
    collaborators: {
        type: Array,
        required: true,
    },
})

function initials(name) {
    if (!name) { return '?' }
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
}

const AVATAR_COLORS = [
    '#4F6D9F', '#2A7B5E', '#7B4F9A', '#9A6B2A',
    '#2A5F7B', '#7B2A2A', '#2A7B4F', '#5F4F7B',
]

function avatarColor(name) {
    if (!name) { return AVATAR_COLORS[0] }
    const code = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return AVATAR_COLORS[code % AVATAR_COLORS.length]
}
</script>
